import React, { useState, useEffect, useContext } from "react";
import { Container, Card, CardBody } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { getOrdersByCourierId } from '../../../api/order/orderApi';
import { createOrder } from '../../../api/order/orderApi';
import { getPlantByUserId } from '../../../api/processing plant/plantApi.js';
import Swal from 'sweetalert2';
import { motion } from 'framer-motion';
import { AuthContext } from '../../ExtraPages/AuthProvider';
import { ArrowRight, Package, MapPin, User } from 'lucide-react';

const CourierAccept = () => {
  const [stockData, setStockData] = useState([]);
  const [plantNames, setPlantNames] = useState({});
  const [plantLocation, setPlantLocation] = useState({});
  const { user, loading } = useContext(AuthContext);
  const [userId, setUserId] = useState(null);

  // Set userId when user is available
  useEffect(() => {
    if (user && user.uid) {
      setUserId(user.uid);
    }
  }, [user]);

  // Fetch stock data and plant names when userId is available
  useEffect(() => {
    if (!userId) return;

    const fetchPlantsStockData = async () => {
      try {
        console.log('Fetching stock data for userId:', userId);
        const response = await getOrdersByCourierId(userId);
        console.log('Fetched orders:', response.data);

        if (Array.isArray(response.data.data)) {
          setStockData(response.data.data);

          // Extract unique Processing_Plant_Id values
          const plantIds = [
            ...new Set(
              response.data.data
                .map((order) => order.Processing_Plant_Id)
                .filter((id) => id)
            ),
          ];

          // Fetch plant names for all plant IDs
          const plantNameMap = {};
          const plantNameLocation = {};
          for (const plantId of plantIds) {
            try {
              const plantResponse = await getPlantByUserId(plantId);
              console.log('plantResponse', plantResponse.data.location);
              plantNameMap[plantId] = plantResponse.data.companyName || 'N/A';
              plantNameLocation[plantId] = plantResponse.data.location || 'N/A';
            } catch (error) {
              console.error(
                `Error fetching plant name for ID ${plantId}:`,
                error.message
              );
              plantNameMap[plantId] = 'N/A';
              plantNameLocation[plantId] = 'N/A';
            }
          }
          setPlantNames(plantNameMap);
          setPlantLocation(plantNameLocation);
        } else {
          console.error('Unexpected response format:', response.data);
          setStockData([]);
        }
      } catch (error) {
        console.error('Error fetching orders:', error.message);
        setStockData([]);
      }
    };

    fetchPlantsStockData();
  }, [userId]);

  const handleAccept = async (id) => {
    Swal.fire({
      title: 'Set Estimated Time and Courier Fee',
      html: `
        <div style="display: flex; justify-content: center; align-items: center; gap: 10px;">
          <input type="number" id="days" class="swal2-input" placeholder="Days" min="0" 
            style="width: 100px; height: 35px; font-size: 14px; text-align: center;">
          <input type="number" id="hours" class="swal2-input" placeholder="Hours" min="0" max="23" 
            style="width: 100px; height: 35px; font-size: 14px; text-align: center;">
          <input type="number" id="courierFee" class="swal2-input" placeholder="Courier Fee" min="0" step="0.01"
            style="width: 120px; height: 35px; font-size: 14px; text-align: center;">
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Set & Accept',
      preConfirm: () => {
        const days = parseInt(document.getElementById('days').value) || 0;
        const hours = parseInt(document.getElementById('hours').value) || 0;
        const courierFee =
          parseFloat(document.getElementById('courierFee').value) || 0;
        return { totalHours: days * 24 + hours, courierFee };
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        const { totalHours, courierFee } = result.value;
        const data = [
          {
            Order_id: id,
            Courier_Status: true,
            EDT: totalHours,
            Courier_Fee: courierFee,
          },
        ];

        try {
          const response = await createOrder(data);
          console.log('Order created:', response.data);
          Swal.fire('Accepted!', 'The stock has been accepted.', 'success');

          // Refresh stock data
          const response1 = await getOrdersByCourierId(userId);
          if (Array.isArray(response1.data.data)) {
            setStockData(response1.data.data);
          } else {
            setStockData([]);
          }
        } catch (error) {
          console.error('Error creating order:', error);
          Swal.fire('Error!', 'Something went wrong.', 'error');
        }
      }
    });
  };

  const handleReject = async (id) => {
    const data = { Order_id: id, Courier_Fee: false };

    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, reject it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await createOrder(data);
          console.log('Order rejected:', response.data);
          Swal.fire('Rejected!', 'The stock has been rejected.', 'success');

          // Refresh stock data
          const response1 = await getOrdersByCourierId(userId);
          if (Array.isArray(response1.data.data)) {
            setStockData(response1.data.data);
          } else {
            setStockData([]);
          }
        } catch (error) {
          console.error('Error rejecting order:', error);
          Swal.fire('Error!', 'Something went wrong.', 'error');
        }
      }
    });
  };

  const handleUpload = async (id) => {
    // Predefined document types
    const documentTypes = [
      'Air waybill',
      'Commercial invoice',
      'Packing List',
      'Catch Certificate(s)',
      'Health Certificate(s)',
      'Preference Certificate',
    ];

    // State to manage files and previews for each document type
    let documents = documentTypes.map((type) => ({
      docType: type,
      file: null,
      preview: null,
    }));

    // Function to generate HTML for document inputs
    const generateDocumentInputs = () => {
      return documents
        .map(
          (doc, index) => `
        <div class="document-entry" style="border-bottom: 1px solid #eee; padding: 10px 0; margin-bottom: 10px;" data-index="${index}">
          <div class="d-flex flex-column gap-3">
            <!-- Document Type Label -->
            <div class="w-100">
              <label for="file-${index}" class="form-label fw-semibold text-muted small">
                ${doc.docType}
              </label>
              <input type="file" id="file-${index}" class="form-control form-control-sm" 
                     accept=".pdf,.doc,.docx,.jpg,.png">
              <div class="form-text text-warning">
                Allowed formats: PDF, DOC, DOCX, JPG, PNG
              </div>
            </div>

            <!-- Preview -->
            <div id="preview-${index}" class="w-100" style="margin-top: 10px;">
              ${
                doc.preview
                  ? doc.preview.startsWith('data:image')
                    ? `<img src="${doc.preview}" style="max-width: 100px; max-height: 100px; object-fit: cover; border-radius: 4px;" />`
                    : `<div class="text-muted small">${
                        doc.file?.name || 'No file selected'
                      }</div>`
                  : ''
              }
            </div>
          </div>
        </div>
      `
        )
        .join('');
    };

    // Show the modal with inputs for each document type
    Swal.fire({
      title: 'Upload Documents',
      html: `
        <div id="document-container" class="d-flex flex-column gap-3">
          ${generateDocumentInputs()}
        </div>
      `,
      showCancelButton: true,
      width: '40%',
      confirmButtonText: 'Upload',
      focusConfirm: false,
      didOpen: () => {
        // Load pdf.js dynamically if not already loaded
        const loadPdfJs = () => {
          if (window.pdfjsLib) return Promise.resolve();
          return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src =
              'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.9.359/pdf.min.js';
            script.onload = () => {
              window.pdfjsLib.GlobalWorkerOptions.workerSrc =
                'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.9.359/pdf.worker.min.js';
              resolve();
            };
            document.head.appendChild(script);
          });
        };

        // Function to update preview
        const updatePreview = async (index) => {
          const fileInput = document.getElementById(`file-${index}`);
          const previewDiv = document.getElementById(`preview-${index}`);
          if (fileInput.files[0]) {
            const file = fileInput.files[0];
            documents[index].file = file;

            if (file.type.startsWith('image/')) {
              // Handle image preview
              const reader = new FileReader();
              reader.onload = (e) => {
                documents[index].preview = e.target.result;
                previewDiv.innerHTML = `<img src="${e.target.result}" style="max-width: 100px; max-height: 100px; object-fit: cover; border-radius: 4px;" />`;
              };
              reader.readAsDataURL(file);
            } else if (file.type === 'application/pdf') {
              // Handle PDF preview
              try {
                await loadPdfJs();
                const reader = new FileReader();
                reader.onload = async (e) => {
                  const arrayBuffer = e.target.result;
                  const pdf = await window.pdfjsLib.getDocument({
                    data: arrayBuffer,
                  }).promise;
                  const page = await pdf.getPage(1);
                  const viewport = page.getViewport({ scale: 0.5 }); // Adjust scale for preview size
                  const canvas = document.createElement('canvas');
                  const context = canvas.getContext('2d');
                  canvas.height = viewport.height;
                  canvas.width = viewport.width;

                  await page.render({
                    canvasContext: context,
                    viewport: viewport,
                  }).promise;

                  documents[index].preview = canvas.toDataURL('image/png');
                  previewDiv.innerHTML = `<img src="${documents[index].preview}" style="max-width: 100px; max-height: 100px; object-fit: cover; border-radius: 4px;" />`;
                };
                reader.readAsArrayBuffer(file);
              } catch (error) {
                console.error('Error generating PDF preview:', error);
                documents[index].preview = file.name;
                previewDiv.innerHTML = `<div class="text-muted small">${file.name}</div>`;
              }
            } else {
              // Handle non-image, non-PDF files (e.g., DOC, DOCX)
              documents[index].preview = file.name;
              previewDiv.innerHTML = `<div class="text-muted small">${file.name}</div>`;
            }
          } else {
            documents[index].file = null;
            documents[index].preview = null;
            previewDiv.innerHTML = '';
          }
        };

        // Attach listeners for file inputs
        documents.forEach((_, index) => {
          const fileInput = document.getElementById(`file-${index}`);
          fileInput.addEventListener('change', () => updatePreview(index));
        });
      },
      preConfirm: () => {
        // Collect form data
        const updatedDocuments = documents
          .map((doc, index) => ({
            docType: doc.docType,
            file: document.getElementById(`file-${index}`).files[0],
          }))
          .filter((doc) => doc.file); // Filter entries with files

        if (updatedDocuments.length === 0) {
          Swal.showValidationMessage('Please upload at least one document');
          return false;
        }

        return updatedDocuments;
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        const uploadedDocuments = result.value;
        try {
          // TODO: Implement actual S3 upload logic here
          for (const doc of uploadedDocuments) {
            console.log(`Uploading ${doc.docType} for order ${id}:`, doc.file);
            // Example:
            // const presignedUrl = await getPresignedUrlFromApi(id, doc.docType, doc.file.name);
            // await fetch(presignedUrl, { method: 'PUT', body: doc.file });
          }
          Swal.fire(
            'Uploaded!',
            'The documents have been uploaded to S3.',
            'success'
          );
        } catch (error) {
          console.error('Error uploading documents:', error);
          Swal.fire('Error!', 'Something went wrong during upload.', 'error');
        }
      }
    });
  };

  const columns = [
    { name: 'Fish Type', selector: (row) => row.Product_Name, sortable: true },
    { name: 'Quantity', selector: (row) => row.quantity, sortable: true },
    { name: 'Vessel Name', selector: (row) => row.boat_number, sortable: true },
    { name: 'Harbour', selector: (row) => row.Harbour, sortable: true },
    { name: 'Order Date', selector: (row) => row.Order_Date, sortable: true },
    {
      name: 'Actions',
      cell: (row) => {
        if (row.Courier_Status === 'false') {
          return <span className="status-button-danger">Rejected</span>;
        } else if (row.Courier_Status === 'true') {
          return (
            <>
              <span className="status-button-success">Accepted</span>
              <button
                className="table-button table-button-info"
                style={{ marginLeft: '8px' }}
                size="sm"
                onClick={() => handleUpload(row.Order_id)}
              >
                Upload Doc
              </button>
            </>
          );
        } else {
          return (
            <>
              <button
                className="table-button table-button-success"
                style={{ marginRight: '4px' }}
                size="sm"
                onClick={() => handleAccept(row.Order_id)}
              >
                Accept
              </button>
              {/* <button
                className="table-button table-button-danger"
                size="sm"
                onClick={() => handleReject(row.Order_id)}
              >
                Reject
              </button> */}
            </>
          );
        }
      },
    },
  ];

  const courierCardStyle = {
    backgroundColor: '#f8f9fa',
    border: '2px solid #e9ecef',
    borderRadius: '12px',
    padding: '20px',
    margin: '20px 0',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  };

  const locationCardStyle = {
    backgroundColor: '#fff',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    padding: '16px',
    minWidth: '450px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  };

  const ExpandableRowComponent = ({ data }) => (
    <div
      style={{
        backgroundColor: '#dfd9d9ff',
        padding: '20px',
        fontFamily: 'Arial, sans-serif',
        color: '#333',
      }}
    >
      <h6 style={{ fontSize: '18px', fontWeight: 'bold' }}>
        Additional Order Details
      </h6>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '40px' }}>
        <div style={{ flex: '1', minWidth: '280px' }}>
          <div style={rowStyle}>
            <strong>Buyer Email:</strong>
            <span>{data.buyerDetails[0]?.email || 'N/A'}</span>
          </div>
          <div style={rowStyle}>
            <strong>Buyer Phone:</strong>
            <span>{data.buyerDetails[0]?.contactNumber || 'N/A'}</span>
          </div>
          <div style={rowStyle}>
            <strong>Processing Fee:</strong>
            <span>{data.Processing_Fee || 'N/A'}</span>
          </div>
          <div style={rowStyle}>
            <strong>Courier Fee:</strong>
            <span>{data.Courier_Fee || 'N/A'}</span>
          </div>
        </div>

        <div style={{ flex: '1', minWidth: '280px' }}>
          <div style={rowStyle}>
            <strong>Processing Types:</strong>
            <div>
              {(data.Processing_Type || 'N/A').split(',').map((type, index) => (
                <span
                  key={index}
                  style={{
                    display: 'inline-block',
                    backgroundColor: 'rgb(0, 59, 143)',
                    color: '#fff',
                    padding: '4px 12px',
                    borderRadius: '15px',
                    marginRight: '6px',
                    marginBottom: '4px',
                    fontSize: '12px',
                  }}
                >
                  {type.trim()}
                </span>
              ))}
            </div>
          </div>
          <div style={rowStyle}>
            <strong>EPT:</strong>
            <span>
              {data.EPT
                ? `${Math.floor(data.EPT / 24)} day(s) ${data.EPT % 24} hour(s)`
                : 'N/A'}
            </span>
          </div>
          <div style={rowStyle}>
            <strong>EDT:</strong>
            <span>
              {data.EDT
                ? `${Math.floor(data.EDT / 24)} day(s) ${data.EDT % 24} hour(s)`
                : 'N/A'}
            </span>
          </div>
          <div style={rowStyle}>
            <strong>Quality Grade:</strong>
            <span>{data.Quality_Grade || 'N/A'}</span>
          </div>
        </div>
      </div>

      <div style={courierCardStyle}>
        <h6
          style={{
            fontSize: '16px',
            fontWeight: 'bold',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Package size={20} />
          Courier Delivery Flow
        </h6>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '20px',
            flexWrap: 'wrap',
          }}
        >
          {/* FROM - Plant Details */}
          <div style={locationCardStyle}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px',
                color: '#28a745',
              }}
            >
              <MapPin size={18} />
              <strong>FROM</strong>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <strong>Plant:</strong>
              <div style={{ color: '#666', fontSize: '14px' }}>
                {plantNames[data.Processing_Plant_Id] || 'N/A'}
              </div>
            </div>
            <div>
              <strong>Location:</strong>
              <div style={{ color: '#666', fontSize: '14px' }}>
                {plantLocation[data.Processing_Plant_Id] || 'N/A'}
              </div>
            </div>
          </div>

          {/* Arrow */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              minWidth: '10px',
            }}
          >
            <ArrowRight
              size={80}
              style={{
                color: '#007bff',
                strokeWidth: 4,
              }}
            />
            <div
              style={{
                fontSize: '12px',
                color: '#007bff',
                fontWeight: 'bold',
                textAlign: 'center',
              }}
            >
              {data.Courier_Method || 'Delivery'}
            </div>
            <div
              style={{
                fontSize: '16px',
                color: '#666',
                textAlign: 'center',
              }}
            >
              Fee: {data.Courier_Fee || 'N/A'} LKR
            </div>
          </div>

          {/* TO - Buyer Details */}
          <div style={locationCardStyle}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px',
                color: '#dc3545',
              }}
            >
              <User size={18} />
              <strong>TO</strong>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <strong>Buyer:</strong>
              <div style={{ color: '#666', fontSize: '14px' }}>
                {data.buyerDetails[0]?.firstName +
                  ' ' +
                  data.buyerDetails[0]?.lastName || 'N/A'}
              </div>
            </div>
            <div>
              <strong>Address:</strong>
              <div style={{ color: '#666', fontSize: '14px' }}>
                {(data.buyerDetails[0]?.address?.line1 || '') +
                  ' ' +
                  (data.buyerDetails[0]?.address?.line2 || '') || 'N/A'}
              </div>
            </div>
          </div>
        </div>

        {/* Delivery Info */}
        <div
          style={{
            marginTop: '20px',
            padding: '12px',
            backgroundColor: '#e3f2fd',
            borderRadius: '6px',
            textAlign: 'center',
          }}
        >
          <strong>Expected Delivery Date: </strong>
          <span style={{ color: '#1976d2' }}>
            {data.Delivery_Date || 'N/A'}
          </span>
        </div>
      </div>
    </div>
  );

  const rowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
    borderBottom: '1px solid #eee',
    paddingBottom: '6px',
    fontSize: '15px',
  };

  if (loading) {
    return (
      <Container fluid>
        <Card>
          <CardBody>
            <h5>Loading...</h5>
          </CardBody>
        </Card>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container fluid>
        <Card>
          <CardBody>
            <h5>Please log in to view courier approvals.</h5>
          </CardBody>
        </Card>
      </Container>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      <div style={{ minHeight: '100%' }}>
        <Container fluid>
          <div style={{ overflowX: 'auto' }}>
            <Card style={{ backgroundColor: '#fff' }}>
              <CardBody style={{ padding: '20px' }}>
                <h5 className="mb-4">Courier Approval</h5>
                <DataTable
                  columns={columns}
                  data={stockData}
                  pagination
                  expandableRows
                  expandableRowsComponent={ExpandableRowComponent}
                />
              </CardBody>
            </Card>
          </div>
        </Container>
      </div>
    </motion.div>
  );
};

export default CourierAccept;